/**
 * Pre-edit snapshot for a Quick Entry row, so Cancel can put the row back the way
 * it was (Spec #34101 / F1c's Cancel affordance; fixed by AB#35152).
 *
 * A Quick Entry row's value lives in one of two places, and Cancel has to be able
 * to restore either:
 *
 *   - `form`  - redux-form DATA_ENTRY_FIELDS_FORM fields (Case Number, Request
 *               Type/Purpose, Authorization Date, Request Date, Clinic/Facility),
 *               restored by dispatching writeFieldToForm per field.
 *   - `state` - DataEntryDetails' own optimistic-local slices (Records Requested's
 *               quickEntryReleaseInfo/Comments, Sensitive Information's
 *               quickEntrySensitiveInfo, the Service Date pair's
 *               quickEntryServiceDateOverride), restored with a single setState.
 *
 * Extracted from DataEntryDetails so the once-only capture rule has a test that can
 * actually execute - the ROIS-UX enzyme `mount` suites do not run on the current
 * CRA 3.4.1 / Jest 24 / enzyme 3.11 / React 17 toolchain.
 */
export interface QuickEntryEditSnapshot {
    readonly form: Readonly<Record<string, any>>;
    readonly state: Readonly<Record<string, any>>;
}

/**
 * Captures the pre-edit value of `formFields` / `stateKeys` - but ONLY on the first
 * call of an edit session. An existing snapshot is returned untouched.
 *
 * The once-only rule is the whole point (AB#35152). DataEntryDetails calls its
 * onEditQuickEntryRow from two places: the row's [Edit] button, and every field's
 * own onChange (which is what keeps a row from unmounting mid-edit once its status
 * flips to Confirmed - see CaseNumberField's onCaseNumberChange). The second caller
 * fires AFTER the field has already written the new value, so re-capturing there
 * would snapshot the edit itself and Cancel would "restore" what the specialist
 * just typed. Callers clear the snapshot on Confirm, which is what lets the next
 * [Edit] capture a fresh one.
 *
 * Values are copied by reference. Every slice this snapshots is replaced wholesale
 * rather than mutated in place (setState with a fresh array/object), so a reference
 * copy is a correct snapshot and there is nothing to deep-clone.
 *
 * Never logs a value - these carry PHI-adjacent form data
 * (.claude/rules/hipaa-compliance.md).
 */
export function takeQuickEntryEditSnapshot(
    existing: QuickEntryEditSnapshot | undefined,
    formFields: readonly string[],
    stateKeys: readonly string[],
    formValues: Readonly<Record<string, any>>,
    stateValues: Readonly<Record<string, any>>,
): QuickEntryEditSnapshot {
    if (existing) {
        return existing;
    }

    const form: Record<string, any> = {};
    // Assigned unconditionally, not guarded on presence: a field that started empty
    // must snapshot as undefined so Cancel actively clears it again, rather than
    // leaving whatever was typed in place.
    formFields.forEach(field => { form[field] = formValues[field]; });

    const state: Record<string, any> = {};
    // Same reasoning, and null is meaningful here - it is the "not touched this
    // transaction, derive from props" sentinel these slices use.
    stateKeys.forEach(key => { state[key] = stateValues[key]; });

    return { form, state };
}

export default takeQuickEntryEditSnapshot;
